import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  doctorsInBrief,
  patientPath,
  patientShowOf,
  type Patient,
  type PatientShow,
  type PatientSummary,
} from '@/domain';
import { usePatientSummary, usePatients } from '@/application/usecases/useClients';
import { useSession } from '@/application/usecases/useSession';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import { KpiCard, KpiGrid } from '@/ui/data/KpiCard';
import { Pagination } from '@/ui/data/Pagination';
import { ResponsiveTable, type Column } from '@/ui/data/ResponsiveTable';
import { SortSelect } from '@/ui/data/SortSelect';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { PatientIcon, PlusIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { readFailure } from './clientRefusal';
import { CLIENT_SORT_PRESETS, clientSortOfPreset } from './clientSorts';
import { PatientFormSheet } from './PatientFormSheet';
import { useListAddress } from './useListAddress';
import styles from './ClientsList.module.css';

/** The counts that are cards, after Total, in the order they are drawn. A patient is never "returning". */
const CARDS = ['newThisWeek', 'newThisMonth', 'withActiveCases'] as const;

/** What each narrowing of the list is called: on its card, and beside the count while it is on. */
const SHOW_LABEL: Record<PatientShow, string> = {
  newThisWeek: strings.kpi.newThisWeek,
  newThisMonth: strings.kpi.newThisMonth,
  withActiveCases: strings.kpi.withActiveCases,
};

/**
 * Patients. The request of 6 October 2026 (second), item 4, as the request of
 * 6 October 2026 (third), items 2 and 3, changed it.
 *
 * Every patient, one record each whichever doctors they are seen by: the ID,
 * the name, the doctors the patient has been to and how many cases are in hand
 * out of how many there have been. A row has room for two doctors' names, the
 * two with the most of the patient's cases, and says how many more there are;
 * the profile lists them all. A row opens the patient's profile.
 *
 * Searched, ordered and paged by the server, as the Doctors list is, with the
 * same four orders and the choice remembered on the device. The search finds a
 * patient by name or by ID (`PT-00031`, `pt 31`, or the number alone).
 *
 * Add patient, for whoever may change the records, asks for the name and
 * nothing else: a case is what ties a patient to a doctor.
 *
 * Since the request of 6 October 2026 (fifth), section 1: four counts above
 * the filter bar, the server's, on the lab's calendar (Total, New this week,
 * New this month, With active cases), each a card that narrows the list to
 * the patients it counts and widens it again; and the search, the count the
 * list is narrowed to, the order and the page are in the address
 * (`useListAddress`), so Back from a patient finds the list as it was left.
 */
export function PatientsScreen() {
  const navigate = useNavigate();
  const session = useSession();
  const mayManage = session.data?.permissions.includes('manageClients') === true;

  const address = useListAddress();
  const order = useTableOrder<Patient>('patients', CLIENT_SORT_PRESETS);
  // The size of a page is the device's to remember; which page it is on is the address's.
  const paging = useTablePage('patients');
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('patients');
  const [adding, setAdding] = useState(false);

  const { q, page } = address;
  // A word in the address that is no count of this list narrows nothing.
  const show = patientShowOf(address.show);
  // The order the address names, when it is one of the four; otherwise the one the device remembers.
  const presetKey =
    CLIENT_SORT_PRESETS.find((preset) => preset.key === address.sort)?.key ?? order.presetKey;
  const sort = clientSortOfPreset(presetKey);

  const summary = usePatientSummary();
  const counts = summary.data;
  // A count that could not be read is an empty card, not one that waits for ever.
  const countOf = (key: keyof PatientSummary) => (summary.isError ? null : counts?.[key]);

  const patients = usePatients(
    useMemo(
      () => ({ q, sort, ...(show === null ? {} : { show }), page, pageSize: paging.pageSize }),
      [q, sort, show, page, paging.pageSize],
    ),
  );
  const { data, isPending, isError, isPlaceholderData } = patients;
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  // A page past the end, from an address older than the list: the last page there is.
  const { setPage } = address;
  useEffect(() => {
    if (data === undefined || isPlaceholderData) return;
    if (data.items.length === 0 && data.total > 0 && page > 1) {
      setPage(Math.ceil(data.total / paging.pageSize));
    }
  }, [data, isPlaceholderData, page, paging.pageSize, setPage]);

  function chooseOrder(key: string) {
    order.choose(key);
    // The first of the four is what the address means by naming none.
    address.setSort(key === CLIENT_SORT_PRESETS[0]?.key ? null : key);
  }

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.clients.searchPatients,
      placeholder: strings.clients.searchPatientsPlaceholder,
      value: address.text,
      onChange: address.setText,
    },
  ];

  const columns: Column<Patient>[] = [
    {
      // The ID leads the row, in the face the job order number is set in.
      key: 'code',
      label: strings.clients.colId,
      mono: true,
      nowrap: true,
      render: (patient) => patient.code,
    },
    {
      key: 'name',
      label: strings.clients.colPatient,
      render: (patient) => <span className={styles.name}>{patient.name}</span>,
    },
    {
      // Plain text, not links: the row is the thing to press, and the profile links to each doctor.
      key: 'doctors',
      label: strings.clients.colDoctors,
      render: (patient) => {
        const brief = doctorsInBrief(patient.doctors);
        if (brief.shown.length === 0) {
          return <span className={styles.unset}>{strings.clients.noDoctorYet}</span>;
        }
        return (
          <span className={styles.cell}>
            {brief.shown.map((doctor) => doctor.name).join(', ')}
            {brief.more > 0 && (
              <span className={styles.more}> {strings.clients.moreDoctors(brief.more)}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'cases',
      label: strings.clients.colCases,
      mono: true,
      nowrap: true,
      align: 'right',
      render: (patient) => strings.clients.activeOfAll(patient.activeCaseCount, patient.caseCount),
    },
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    {
      // When the newest of the patient's cases was taken in.
      key: 'lastCase',
      label: strings.table.columns.lastCase,
      mono: true,
      nowrap: true,
      optional: true,
      render: (patient) =>
        patient.lastCaseAt === null ? (
          <span className={styles.unset}>{strings.clients.notSet}</span>
        ) : (
          formatDay(patient.lastCaseAt)
        ),
    },
    {
      key: 'added',
      label: strings.table.columns.added,
      mono: true,
      nowrap: true,
      optional: true,
      render: (patient) => formatDay(patient.createdAt),
    },
  ];

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.clients.label}</span>
        <h2>{strings.clients.patientsTitle}</h2>
        <span className={styles.hint}>{strings.clients.patientsHint}</span>
        {mayManage && (
          <>
            <span className={styles.spacer} />
            <Button variant="primary" icon={<PlusIcon />} onClick={() => setAdding(true)}>
              {strings.clients.addPatient}
            </Button>
          </>
        )}
      </div>

      <KpiGrid label={strings.kpi.patientsLabel}>
        <KpiCard
          label={strings.kpi.total}
          value={countOf('total')}
          // Everybody: pressed while no count narrows the list.
          pressed={show === null}
          onPress={() => address.setShow(null)}
        />
        {CARDS.map((card) => (
          <KpiCard
            key={card}
            label={SHOW_LABEL[card]}
            value={countOf(card)}
            pressed={show === card}
            onPress={() => address.setShow(show === card ? null : card)}
          />
        ))}
      </KpiGrid>

      <FilterBar
        label={strings.clients.patientFiltersLabel}
        filters={filters}
        sort={<SortSelect presets={CLIENT_SORT_PRESETS} value={presetKey} onChange={chooseOrder} />}
      />

      {isError && (
        <p className={styles.error} role="alert">
          {readFailure(patients.error)}
          <Button onClick={() => void patients.refetch()}>{strings.errors.retry}</Button>
        </p>
      )}

      {isPending && <SkeletonRows rows={6} label={strings.clients.loadingPatients} />}

      {!isPending && !isError && rows.length === 0 && (
        <EmptyState
          icon={<PatientIcon />}
          heading={
            q !== ''
              ? strings.clients.noPatientMatches
              : show !== null
                ? strings.clients.noPatientsShown
                : strings.clients.noPatients
          }
          detail={
            q !== ''
              ? strings.clients.noMatchDetail
              : show !== null
                ? strings.clients.noShownDetail
                : strings.clients.noPatientsDetail
          }
          // The next step out of a count nobody is in.
          action={
            show === null ? undefined : (
              <Button onClick={() => address.setShow(null)}>{strings.kpi.clearFilter}</Button>
            )
          }
        />
      )}

      {!isPending && !isError && rows.length > 0 && (
        <>
          <div className={styles.count}>
            <b>{total.toLocaleString()}</b> {strings.clients.patientsCounted(total)}
            {show !== null && (
              <span className={styles.narrowed}>
                <span>{strings.kpi.filterOn(SHOW_LABEL[show])}</span>
                <button
                  type="button"
                  className={styles.clearFilter}
                  onClick={() => address.setShow(null)}
                >
                  {strings.kpi.clearFilter}
                </button>
              </span>
            )}
          </div>
          <ResponsiveTable
            customize={{ layout: layout.layout, save: layout.save, reset: layout.reset, rows: { size: paging.pageSize, sizes: PAGE_SIZES, choose: paging.choose } }}
            columns={columns}
            rows={rows}
            rowKey={(patient) => patient.id}
            caption={strings.clients.patientsCaption}
            onRowClick={(patient) => void navigate(patientPath(patient.id))}
          />
          <Pagination
            page={page}
            pageSize={paging.pageSize}
            total={total}
            sizes={PAGE_SIZES}
            onPage={address.setPage}
            onPageSize={(size) => {
              paging.choose(size);
              address.setPage(1);
            }}
            label={strings.table.pagesLabel}
          />
        </>
      )}

      {adding && (
        <PatientFormSheet
          onClose={() => setAdding(false)}
          onDone={(patient) => {
            setAdding(false);
            void navigate(patientPath(patient.id));
          }}
        />
      )}
    </div>
  );
}
